import React, { useMemo } from "react";
import { Badge, Card, ListGroup } from "react-bootstrap";
import type {
  DashboardAnalytics,
  DashboardPeriodLabel,
} from "../../types/dashboard";

interface TopMoviesListProps {
  analytics: DashboardAnalytics | null | undefined;
  period: DashboardPeriodLabel;
  cinemaName: string | null;
  cinemaId: string | null;
  dateRange?: { dateFrom: string; dateTo: string };
}

interface MovieItem {
  id: string | number;
  name: string;
  genre?: string;
  revenue: number;
  bookings: number;
}

const normalizeGenres = (genre?: string): string[] => {
  if (!genre) return [];
  if (Array.isArray(genre)) return (genre as string[]).filter(Boolean);
  if (typeof genre !== "string") return [];
  const trimmed = genre.trim();
  if (!trimmed || trimmed === "Unknown") return [];
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return trimmed
      .replace(/^\{/, "")
      .replace(/\}$/, "")
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .split(",")
      .map((g) => g.replace(/^"|"$/g, "").replace(/\\"/g, "").trim())
      .filter(Boolean);
  }
  return trimmed
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean);
};

const formatCurrency = (n: number): string => {
  if (!Number.isFinite(n)) return "₹0";
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1)}Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(1)}L`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const periodHeading: Record<DashboardPeriodLabel, string> = {
  today: "Today",
  this_week: "This Week",
  this_month: "This Month",
  this_year: "This Year",
  custom: "Custom Range",
  all: "All Time",
};

const TopMoviesList: React.FC<TopMoviesListProps> = ({
  analytics,
  period,
  cinemaName,
  cinemaId,
}) => {
  const movies: MovieItem[] = useMemo(() => {
    if (!analytics) return [];
    if (period === "today") return (analytics.topMoviesToday as MovieItem[]) ?? [];
    if (period === "this_week")
      return (analytics.topMoviesThisWeek as MovieItem[]) ?? [];
    return (analytics.topMovies as MovieItem[]) ?? [];
  }, [analytics, period]);

  const heading = useMemo(() => {
    const base = `Top Movies · ${periodHeading[period]}`;
    return cinemaId && cinemaName ? `${base} · ${cinemaName}` : base;
  }, [period, cinemaId, cinemaName]);

  const emptyMessage = useMemo(() => {
    if (cinemaId) return "No bookings for this cinema in this period yet.";
    if (period === "today") return "No bookings today yet.";
    if (period === "this_week") return "No bookings this week yet.";
    return "Top movies are only computed for Today and This Week. Switch to either to see them.";
  }, [period, cinemaId]);

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">{heading}</h6>
        <Badge bg="light" text="dark">
          {periodHeading[period]}
        </Badge>
      </Card.Header>
      <Card.Body className="p-0">
        {!analytics ? (
          <div className="text-center text-muted py-4">
            <div
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            <div className="small mt-2">Loading…</div>
          </div>
        ) : movies.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="bi-film fs-1 opacity-25 d-block mb-2" />
            <small>{emptyMessage}</small>
          </div>
        ) : (
          <ListGroup variant="flush">
            {movies.map((movie, idx) => {
              const genres = normalizeGenres(movie.genre);
              return (
                <ListGroup.Item
                  key={String(movie.id) || idx}
                  className="d-flex align-items-center py-2"
                >
                  <div
                    className="me-3 d-flex align-items-center justify-content-center fw-bold"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background:
                        idx === 0
                          ? "#FFD700"
                          : idx === 1
                            ? "#C0C0C0"
                            : idx === 2
                              ? "#CD7F32"
                              : "#E9ECEF",
                      color: idx <= 2 ? "#fff" : "#6c757d",
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-grow-1 me-2 overflow-hidden">
                    <div
                      className="fw-medium text-truncate"
                      title={movie.name}
                    >
                      {movie.name}
                    </div>
                    {genres.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {genres.map((g) => (
                          <span
                            key={g}
                            className="rounded-pill"
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: 0.2,
                              padding: "4px 10px",
                              lineHeight: 1,
                              backgroundColor: "#f1f3f5",
                              color: "#495057",
                              border: "1px solid #e4e6e8",
                              whiteSpace: "nowrap",
                            }}
                            title={g}
                          >
                            {g}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-end" style={{ minWidth: 90 }}>
                    <div className="fw-medium">
                      {formatCurrency(movie.revenue)}
                    </div>
                    <small className="text-muted">
                      {movie.bookings} booking{movie.bookings === 1 ? "" : "s"}
                    </small>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
};

export default TopMoviesList;